import type { LucideIcon } from 'lucide-react'
import type { BlobShape } from '@/lib/shapes'
import type { ContactTopic } from '@/constants/contact'

import {
    Aperture,
    Bird,
    Blocks,
    Citrus,
    Coffee,
    Compass,
    Cookie,
    Flower2,
    Globe,
    Handshake,
    Leaf,
    Lightbulb,
    MessagesSquare,
    Palette,
    PenTool,
    Receipt,
    Rocket,
    ScanSearch,
    TrendingUp,
    Users,
    Zap
} from 'lucide-react'

export interface Perk {
    title: string
    text: string
    icon: LucideIcon
    shape: BlobShape
    color: string
    accent: string
}

export const PERKS: Perk[] = [
    {
        title: "Fast, minus the fuss",
        text: "Kickoff within a week and first concepts in about ten days. Momentum matters, so we protect it.",
        icon: Zap,
        shape: 'flower',
        color: 'text-mint',
        accent: 'text-butter'
    },
    {
        title: "Prices you can plan around",
        text: "Fixed scopes, flat monthly plans and zero surprise invoices. You'll always know what you're paying for.",
        icon: Receipt,
        shape: 'cloud',
        color: 'text-bubblegum',
        accent: 'text-sky'
    },
    {
        title: "Senior people, start to finish",
        text: "The folks you meet on the first call are the ones doing the work. No quiet hand-offs to a B-team.",
        icon: Users,
        shape: 'burst',
        color: 'text-butter',
        accent: 'text-lavender'
    }
]

export interface Client {
    name: string
    icon: LucideIcon
    color: string
    iconColor: string
}

export const CLIENTS: Client[] = [
    { name: "Bloom Ritual", icon: Flower2, color: 'bg-bubblegum', iconColor: 'text-rose-700' },
    { name: "Bluebird Roasters", icon: Coffee, color: 'bg-sky', iconColor: 'text-sky-800' },
    { name: "Kaleido", icon: Aperture, color: 'bg-lavender', iconColor: 'text-violet-700' },
    { name: "Fizzy Fern", icon: Citrus, color: 'bg-mint', iconColor: 'text-emerald-700' },
    { name: "Northpaw", icon: Compass, color: 'bg-peach', iconColor: 'text-orange-700' },
    { name: "Saltbox Bakery", icon: Cookie, color: 'bg-butter', iconColor: 'text-amber-800' },
    { name: "Oddbird Labs", icon: Bird, color: 'bg-cream', iconColor: 'text-indigo-700' },
    { name: "Wildroot", icon: Leaf, color: 'bg-butter-soft', iconColor: 'text-green-700' }
]

// One full wave across the eight client chips: vertical offset and tilt.
export const CLIENT_WAVE = [
    { y: 0, rotate: -8 },
    { y: -21, rotate: -6 },
    { y: -30, rotate: 0 },
    { y: -21, rotate: 6 },
    { y: 0, rotate: 8 },
    { y: 21, rotate: 6 },
    { y: 30, rotate: 0 },
    { y: 21, rotate: -6 }
]

export interface Stat {
    value: number
    suffix: string
    label: string
}

export const STATS: Stat[] = [
    { value: 65, suffix: '+', label: "Brands launched" },
    { value: 310, suffix: '+', label: "Projects shipped" },
    { value: 12, suffix: '', label: "Countries served" },
    { value: 97, suffix: '%', label: "Clients who come back" }
]

export interface Service {
    title: string
    tagline: string
    description: string
    tags: string[]
    color: string
    topic: ContactTopic
}

export const SERVICES: Service[] = [
    {
        title: "Branding & identity",
        tagline: "Names, logos and visual systems with real personality.",
        description: "We dig into who you are, who you're for and what makes you different, then turn it into a brand that feels unmistakably yours. You get everything from the first sketches to a guidelines book your whole team can actually use.",
        tags: ["Brand strategy", "Naming", "Logo design", "Visual identity", "Packaging", "Brand guidelines"],
        color: 'bg-peach',
        topic: 'Branding & identity'
    },
    {
        title: "Websites & digital products",
        tagline: "Fast, good-looking sites that turn visitors into customers.",
        description: "From a sharp one-pager to a full online shop, we design and build websites that load fast, read clearly and are easy for you to update. Every page is planned around what you want visitors to do next.",
        tags: ["UX & wireframes", "Web design", "Development", "Online shops", "Easy content editing", "SEO setup"],
        color: 'bg-mint',
        topic: 'Website'
    },
    {
        title: "Content & social",
        tagline: "Scroll-stopping content, delivered on a schedule.",
        description: "Photos, short videos, captions and templates that look like they belong together. We plan a month at a time, so you show up consistently without living on your phone.",
        tags: ["Art direction", "Photo & video", "Social templates", "Copywriting", "Motion graphics"],
        color: 'bg-lavender',
        topic: 'Content & social'
    },
    {
        title: "Growth marketing",
        tagline: "Campaigns that bring in the right people, not just more people.",
        description: "Launches, paid social, search and email, all measured against goals we agree on upfront. Every month you get a clear report on what worked, what didn't and what we'll try next.",
        tags: ["Launch campaigns", "Paid social", "SEO", "Email marketing", "Analytics & reporting"],
        color: 'bg-bubblegum',
        topic: 'Growth marketing'
    }
]

export interface Benefit {
    title: string
    text: string
    icon: LucideIcon
    shape: BlobShape
    color: string
    accent: string
    wide?: boolean
}

export const BENEFITS: Benefit[] = [
    {
        title: "Ideas with a pulse",
        text: "We bring concepts that make you a little nervous in the best possible way, then back every one of them with solid thinking about your customers.",
        icon: Lightbulb,
        shape: 'flower',
        color: 'bg-mint',
        accent: 'text-coral',
        wide: true
    },
    {
        title: "Senior team, always",
        text: "The people in the pitch are the people doing the work. No bait and switch.",
        icon: Handshake,
        shape: 'burst',
        color: 'bg-butter',
        accent: 'text-bubblegum'
    },
    {
        title: "Design that sells",
        text: "Pretty is the starting line. Every choice ties back to a business goal.",
        icon: TrendingUp,
        shape: 'cloud',
        color: 'bg-peach',
        accent: 'text-mint'
    },
    {
        title: "Straight talk",
        text: "Weekly updates in plain English and a shared board you can peek at anytime.",
        icon: MessagesSquare,
        shape: 'blob',
        color: 'bg-lavender',
        accent: 'text-butter'
    },
    {
        title: "Built to last",
        text: "Templates and systems your team can keep using long after launch day.",
        icon: Blocks,
        shape: 'gear',
        color: 'bg-sky',
        accent: 'text-peach'
    },
    {
        title: "Global taste, local feel",
        text: "We've worked with brands in 12 countries and know how to speak to each market.",
        icon: Globe,
        shape: 'flower',
        color: 'bg-bubblegum',
        accent: 'text-sky'
    },
    {
        title: "Obsessed with details",
        text: "Kerning, load times, alt text and all the tiny things others skip. We sweat every one of them, so your brand looks sharp everywhere it shows up.",
        icon: ScanSearch,
        shape: 'cloud',
        color: 'bg-butter-soft',
        accent: 'text-lavender',
        wide: true
    }
]

export interface Step {
    title: string
    when: string
    text: string
    deliverables: string[]
    icon: LucideIcon
    shape: BlobShape
    color: string
    accent: string
}

export const STEPS: Step[] = [
    {
        title: "Coffee & questions",
        when: "Week 1",
        text: "We start with a long, curious chat about your business, your customers and what success looks like. You leave with a clear brief, a timeline and a fixed quote.",
        deliverables: ["Kickoff workshop", "Brand & site audit", "Project roadmap"],
        icon: Coffee,
        shape: 'flower',
        color: 'bg-peach',
        accent: 'text-butter'
    },
    {
        title: "Sketch & strategy",
        when: "Weeks 2 to 3",
        text: "We explore a few directions in rough form first. It's the quickest way to find the big idea before anyone polishes a single pixel.",
        deliverables: ["Moodboards", "Concept routes", "Messaging framework"],
        icon: PenTool,
        shape: 'cloud',
        color: 'bg-lavender',
        accent: 'text-mint'
    },
    {
        title: "Design & build",
        when: "Weeks 3 to 6",
        text: "The winning direction gets refined, designed and built, with you weighing in at every milestone. No dramatic big reveals, no nasty surprises.",
        deliverables: ["Identity system", "Website build", "Launch content"],
        icon: Palette,
        shape: 'burst',
        color: 'bg-mint',
        accent: 'text-bubblegum'
    },
    {
        title: "Launch & grow",
        when: "Week 6 and beyond",
        text: "We ship it, celebrate properly, then track the results and keep improving. Plenty of clients stay on a monthly plan after launch.",
        deliverables: ["Launch plan", "Team handover", "Monthly reports"],
        icon: Rocket,
        shape: 'gear',
        color: 'bg-butter',
        accent: 'text-coral'
    }
]

export const COMPARISON = {
    them: [
        "Months of meetings before anything happens",
        "Juniors doing the work you paid seniors for",
        "Surprise invoices for every small change",
        "Jargon-heavy decks nobody reads",
        "The same template they sold the last client",
        "Gone the moment the project launches",
        "Slow replies buried in email threads",
        "Pretty work with no goal behind it"
    ],
    us: [
        "First concepts within two weeks",
        "Senior designers from day one",
        "Flat, fixed pricing agreed upfront",
        "Plain-English updates every week",
        "Work built around your brand only",
        "Support and tweaks after launch",
        "A shared channel with same-day replies",
        "Every design tied to a measurable goal"
    ]
}

export interface Testimonial {
    quote: string
    name: string
    role: string
    color: string
}

export const TESTIMONIALS: Testimonial[] = [
    {
        quote: "Huzzah gave us a website that finally sounds like us. Demo requests jumped 40% in the first month.",
        name: "Priya Nair",
        role: "Head of Marketing, Kaleido",
        color: 'bg-lavender'
    },
    {
        quote: "Fast, funny and frighteningly good at what they do. Our first packaging run sold out before we could restock.",
        name: "Sofia Marin",
        role: "Co-founder, Bloom Ritual",
        color: 'bg-bubblegum'
    },
    {
        quote: "No fluff and no endless decks. Just great ideas, clear prices and a team that picks up the phone when it matters.",
        name: "Daniel Okafor",
        role: "CEO, Northpaw",
        color: 'bg-mint'
    },
    {
        quote: "We came in for a logo and stayed for everything else. They feel like our in-house team, minus the overhead.",
        name: "Hannah Lee",
        role: "Owner, Saltbox Bakery",
        color: 'bg-butter'
    },
    {
        quote: "Our launch video passed two million views in a week. I still don't fully know how they did it, and I'm not asking.",
        name: "Leo Fischer",
        role: "Brand Lead, Fizzy Fern",
        color: 'bg-peach'
    },
    {
        quote: "Every deadline hit and every invoice exactly as quoted. That alone puts them in a league of their own.",
        name: "Grace Kim",
        role: "COO, Oddbird Labs",
        color: 'bg-sky'
    }
]

export const FEATURED_TESTIMONIAL = {
    quote: "They turned our little coffee cart into a brand people happily queue for. Online sales doubled in the first quarter after the rebrand, and our bags finally look as good as the coffee tastes.",
    name: "Marcus Bell",
    role: "Founder, Bluebird Roasters",
    image: '/images/client-1-illustration.png',
    imageAlt: "Illustration of Marcus Bell, founder of Bluebird Roasters, holding a coffee bag",
    projectSlug: 'bluebird-roasters'
}

export interface Plan {
    id: string
    name: string
    description: string
    monthly?: number
    yearly?: number
    oneTime?: number
    features: string[]
    bestFor: string
    color: string
    popular?: boolean
    topic: ContactTopic
}

export const PLANS: Plan[] = [
    {
        id: 'social-spark',
        name: "Social Spark",
        description: "Content and social media, handled every month.",
        monthly: 349,
        yearly: 279,
        features: [
            "12 social posts a month",
            "4 short-form videos",
            "Monthly content calendar",
            "Captions & hashtags written for you",
            "Community management basics",
            "Monthly performance report"
        ],
        bestFor: "Brands that need to show up online every week without hiring a full team.",
        color: 'bg-mint',
        topic: 'Social Spark plan'
    },
    {
        id: 'web-launch',
        name: "Web Launch",
        description: "A custom website, plus the care to keep it great.",
        monthly: 549,
        yearly: 439,
        features: [
            "Custom design, up to 8 pages",
            "Mobile-first build",
            "Easy content editing",
            "SEO & analytics setup",
            "Hosting & maintenance",
            "2 hours of updates every month"
        ],
        bestFor: "Growing businesses ready for a site that works as hard as they do.",
        color: 'bg-lavender',
        popular: true,
        topic: 'Web Launch plan'
    },
    {
        id: 'brand-kickstart',
        name: "Brand Kickstart",
        description: "A complete brand identity in about five weeks.",
        oneTime: 2400,
        features: [
            "Brand strategy workshop",
            "Logo & visual identity",
            "Color & type system",
            "Brand guidelines",
            "Social & stationery templates",
            "2 rounds of revisions"
        ],
        bestFor: "New businesses and rebrands that want to start strong.",
        color: 'bg-peach',
        topic: 'Brand Kickstart plan'
    }
]

export interface TeamMember {
    name: string
    role: string
    nickname: string
    image: string
    imageAlt: string
    imagePosition: string
    color: string
    stickerColor: string
    stickerShape: BlobShape
}

export const TEAM: TeamMember[] = [
    {
        name: "Maya Torres",
        role: "Founder & Creative Director",
        nickname: "Big ideas",
        image: '/images/team-1-illustration.png',
        imageAlt: "Illustration of Maya Torres smiling in a light grey blazer",
        imagePosition: 'object-[50%_30%]',
        color: 'bg-butter',
        stickerColor: 'text-bubblegum',
        stickerShape: 'burst'
    },
    {
        name: "Theo Ramos",
        role: "Lead Designer",
        nickname: "Pixel nerd",
        image: '/images/team-2-illustration.png',
        imageAlt: "Illustration of Theo Ramos grinning in a white t-shirt",
        imagePosition: 'object-[50%_30%]',
        color: 'bg-mint',
        stickerColor: 'text-lavender',
        stickerShape: 'flower'
    },
    {
        name: "Nadia Rahman",
        role: "Head of Web",
        nickname: "Code wizard",
        image: '/images/team-3-illustration.png',
        imageAlt: "Illustration of Nadia Rahman smiling in round glasses",
        imagePosition: 'object-[50%_30%]',
        color: 'bg-lavender',
        stickerColor: 'text-butter',
        stickerShape: 'cloud'
    }
]

export interface Faq {
    question: string
    answer: string
}

export const FAQS: Faq[] = [
    {
        question: "How long does a typical project take?",
        answer: "Most brand identities take four to six weeks and most websites six to ten. Monthly plans start within a week of signing. You'll get a week-by-week timeline before any work begins."
    },
    {
        question: "Can I cancel a monthly plan?",
        answer: "Yes, anytime, with 30 days' notice. There are no long contracts and no cancellation fees. We'd rather keep you because the work is good."
    },
    {
        question: "Do you only work with big brands?",
        answer: "Not at all. Most of our clients are small and growing businesses, from first-time founders to teams of a few hundred. If you care about doing it properly, we're in."
    },
    {
        question: "What do you need from me to get started?",
        answer: "A 30-minute call, your goals and anything you already have, like a logo, photos or an old website. We handle the rest and tell you exactly when we need your input."
    },
    {
        question: "Do I own the final work?",
        answer: "Yes. Once the final invoice is paid, every final file and all the rights to it are yours to keep, change and use however you like."
    },
    {
        question: "Can you work with my existing brand or website?",
        answer: "Absolutely. We often refresh or extend what's already working rather than starting over, and we'll be honest about what to keep and what to change."
    },
    {
        question: "Where are you based?",
        answer: "Our studio is in Portland, Oregon, but most of our work happens remotely with clients all over the world. Time zones have never stopped a good project."
    }
]
